/**
 * ComicFormTracking – GA4 dataLayer analytics module
 *
 * Pushes events to window.dataLayer only. No gtag() calls.
 * Map these event names in GTM as Custom Event triggers → GA4 events.
 *
 * Public API
 * ──────────
 *   ComicFormTracking.init()
 *   ComicFormTracking.onFormRevealed()                – call when hero CTA clicked
 *   ComicFormTracking.onStepEnter(stepName)           – 'character' | 'story'
 *   ComicFormTracking.onStepComplete(stepName)         – 'character' | 'story'
 *   ComicFormTracking.incrementValidationErrors(step)  – 'character' | 'story'
 *   ComicFormTracking.onInspirationRevealed()
 *   ComicFormTracking.onMailCompleted(mailEntered)  – true if a valid email was entered
 *   ComicFormTracking.onAddToCart()
 *   ComicFormTracking.onUploadAttempted(fileSizeBytes, isImage)
 */
(function (window) {
  'use strict';

  // ── Internal state ────────────────────────────────────────────────────────

  var _state = {
    formStartTime:          null,  // performance.now() snapshot at form_started
    stepEntryTimes:         {},    // { character: perf, story: perf }
    charValidationErrors:   0,
    storyValidationErrors:  0,
    inspirationClicksCount: 0,
    usedInspiration:        false,
  };

  // ── Deduplication ─────────────────────────────────────────────────────────
  //
  // ALL conversion funnel events use the same scope: sessionStorage.
  // This guarantees that event counts can never invert in the funnel
  // (character_step_completed can never exceed form_started, etc.) even
  // if the user reloads the page mid-form within the same browser session.
  //
  // _once(name) → true the first time, false every subsequent call this session.
  //
  // inspiration_revealed + character_photo_upload_attempted intentionally
  // bypass _once — they are designed to fire on every occurrence.
  //
  function _once(eventName) {
    var key = 'ga4_once_' + eventName;
    try {
      if (sessionStorage.getItem(key) === '1') return false;
      sessionStorage.setItem(key, '1');
    } catch (_e) { /* sessionStorage unavailable – allow the push */ }
    return true;
  }

  // ── Safe dataLayer push ───────────────────────────────────────────────────

  function _push(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (_e) {
      // Never throw – analytics must never break the form.
    }
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────

  /** Property names for line items (set by Liquid from locale; fallback to Dutch). */
  function _prop() {
    var p = window.__comicFormPropertyNames;
    return p || { character: 'Personage', character_description: 'Beschrijving', story: 'Verhaal' };
  }

  /**
   * Collect character-related parameters from the live DOM.
   * characters_count = visible (non-hidden) named character cards.
   */
  function _getCharacterData() {
    var prop = _prop();
    var count = 0, photoCount = 0, descCount = 0, usedPhoto = false, usedDesc = false;
    for (var i = 1; i <= 6; i++) {
      var card = document.querySelector(
        '.accordion-card[data-char-index="' + i + '"]'
      );
      if (!card || card.classList.contains('char-hidden')) continue;

      var nameInput = document.querySelector(
        'input[name="properties[' + prop.character + ' ' + i + ']"]'
      );
      // Only count characters with a name (incomplete chars don't count)
      if (!nameInput || !nameInput.value.trim()) continue;
      count++;

      var fileInput = document.getElementById('file-character-' + i);
      if (fileInput && fileInput.files && fileInput.files.length > 0) {
        photoCount++;
        usedPhoto = true;
      }

      var descInput = document.querySelector(
        'input[name="properties[' + prop.character + ' ' + i + ' ' + prop.character_description + ']"]'
      );
      if (descInput && descInput.value.trim()) {
        descCount++;
        usedDesc = true;
      }
    }
    return {
      characters_count:  count,
      photo_count:       photoCount,
      description_count: descCount,
      used_photo:        usedPhoto,
      used_description:  usedDesc,
    };
  }

  /** Return trimmed story length from the textarea. */
  function _getStoryLength() {
    var prop = _prop();
    var ta = document.querySelector('textarea[name="properties[' + prop.story + ']"]');
    return ta ? ta.value.trim().length : 0;
  }

  // ── Bucketing helpers ─────────────────────────────────────────────────────

  function _storyLengthBucket(len) {
    if (len < 50)   return 'lt50';
    if (len <= 150) return '50-150';
    if (len <= 400) return '150-400';
    return '400+';
  }

  /**
   * file_size_bucket
   *   small  < 500 KB
   *   medium  500 KB – 2 MB
   *   large  > 2 MB
   *   unknown if size is unavailable
   */
  function _fileSizeBucket(bytes) {
    if (typeof bytes !== 'number' || isNaN(bytes)) return 'unknown';
    if (bytes < 500 * 1024)        return 'small';
    if (bytes <= 2 * 1024 * 1024)  return 'medium';
    return 'large';
  }

  // ── Misc helpers ──────────────────────────────────────────────────────────

  function _deviceType() {
    try {
      return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? 'mobile'
        : 'desktop';
    } catch (_e) {
      return undefined;
    }
  }

  /**
   * Return elapsed seconds (rounded) since a performance.now() snapshot.
   * Returns undefined if the snapshot is not yet set.
   */
  function _elapsed(fromPerf) {
    if (fromPerf == null) return undefined;
    try {
      return Math.round((performance.now() - fromPerf) / 1000);
    } catch (_e) {
      return undefined;
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  var ComicFormTracking = {

    /**
     * Fire form_started when the user begins the form.
     * Only the hero CTA button triggers this (called from liquid when the
     * button is clicked and the form is revealed).
     *
     * If form_started already fired earlier in this browser session (e.g. page
     * reload), formStartTime is updated so downstream timing events remain valid.
     */
    onFormRevealed: function () {
      if (!_once('form_started')) {
        _state.formStartTime = performance.now();
        return;
      }
      _state.formStartTime = performance.now();
      var params = { form_version: 'v1' };
      var dt = _deviceType();
      if (dt) params.device_type = dt;
      _push('form_started', params);
    },

    /**
     * Initialize tracking state. form_started is only fired by the hero CTA
     * button (via onFormRevealed from the liquid). This restores formStartTime
     * on reload so downstream timing events remain valid.
     */
    init: function () {
      try {
        if (sessionStorage.getItem('ga4_once_form_started') === '1') {
          _state.formStartTime = performance.now();
        }
      } catch (_e) { /* ignore */ }
    },

    /**
     * Record the moment a step becomes active so we can compute step_time_seconds
     * at completion. Call whenever a step is shown (including back-navigation –
     * this resets the timer for that step, which is correct).
     *
     * @param {string} stepName  'character' | 'story'
     */
    onStepEnter: function (stepName) {
      _state.stepEntryTimes[stepName] = performance.now();
    },

    /**
     * Increment the validation-error counter for a step.
     * Call every time the forward-navigation button fails validation.
     *
     * @param {string} stepName  'character' | 'story'
     */
    incrementValidationErrors: function (stepName) {
      if (stepName === 'character') _state.charValidationErrors++;
      else if (stepName === 'story') _state.storyValidationErrors++;
    },

    /**
     * Fire character_step_completed or story_step_completed.
     * Call BEFORE showing the next step, after successful validation.
     *
     * @param {string} stepName  'character' | 'story'
     */
    onStepComplete: function (stepName) {
      if (stepName === 'character') {
        if (!_once('character_step_completed')) return;

        var cd       = _getCharacterData();
        var stepTime = _elapsed(_state.stepEntryTimes.character);
        _push('character_step_completed', {
          characters_count:            cd.characters_count,
          used_photo:                  cd.used_photo,
          used_description:            cd.used_description,
          photo_count:                 cd.photo_count,
          description_count:           cd.description_count,
          character_step_time_seconds: stepTime,
          validation_errors_count:     _state.charValidationErrors,
        });
        // Keep error counter (not reset) so total errors across all attempts
        // are preserved in the event that fired.

      } else if (stepName === 'story') {
        if (!_once('story_step_completed')) return;

        var storyLen  = _getStoryLength();
        var storyTime = _elapsed(_state.stepEntryTimes.story);
        _push('story_step_completed', {
          story_length_bucket:       _storyLengthBucket(storyLen),
          used_inspiration:          _state.usedInspiration,
          inspiration_clicks_count:  _state.inspirationClicksCount,
          story_step_time_seconds:   storyTime,
          validation_errors_count:   _state.storyValidationErrors,
        });
      }
    },

    /**
     * Fire inspiration_revealed when the "Need inspiration?" box is toggled open.
     * Call only when the box has just become visible.
     */
    onInspirationRevealed: function () {
      _state.inspirationClicksCount++;
      _state.usedInspiration = true;
      var storyLen = _getStoryLength();
      _push('inspiration_revealed', {
        inspiration_clicks_count:          _state.inspirationClicksCount,
        story_length_before_reveal_bucket: _storyLengthBucket(storyLen),
      });
    },

    /**
     * Fire mail_completed when the user finishes the email step.
     * Email is mandatory, so this is always true in the current funnel.
     * @param {boolean} mailEntered  true if a valid email was entered
     */
    onMailCompleted: function (mailEntered) {
      if (!_once('mail_completed')) return;
      _push('mail_completed', {
        mail_step_time_seconds: _elapsed(_state.formStartTime),
        mail_entered: mailEntered === true,
      });
    },

    onAddToCart: function () {
      if (!_once('add_to_cart_clicked')) return;

      var cd        = _getCharacterData();
      var storyLen  = _getStoryLength();
      var totalTime = _elapsed(_state.formStartTime);
      _push('add_to_cart_clicked', {
        characters_count:        cd.characters_count,
        used_photo:              cd.used_photo,
        used_description:        cd.used_description,
        photo_count:             cd.photo_count,
        story_length_bucket:     _storyLengthBucket(storyLen),
        total_form_time_seconds: totalTime,
      });
    },

    /**
     * Fire character_photo_upload_attempted on every file-input change.
     *
     * @param {number}  fileSizeBytes  Raw file size in bytes (before compression).
     * @param {boolean} isImage        true when file.type starts with 'image/'.
     */
    onUploadAttempted: function (fileSizeBytes, isImage) {
      _push('character_photo_upload_attempted', {
        file_size_bucket: _fileSizeBucket(fileSizeBytes),
        upload_success:   isImage === true,
      });
    },
  };

  // Expose globally
  window.ComicFormTracking = ComicFormTracking;

})(window);
